import { IntlShape, MessageDescriptor } from 'react-intl'

import { cloneDeep, isArray, isEmpty, isEqual, omit } from 'lodash-es'

import {
  AVAILABILITY_PREFERENCE_DAY_OF_WEEK_MAPPING,
  AVAILABILITY_PREFERENCE_TIME_OF_DAY_MAPPING,
  CARE_LANGUAGES_PREFERENCE_CATEGORY,
  defaultV2PreferencesForUser,
  defaultV3PreferencesForUser,
  emptyProviderPreferenceState,
  emptyProviderPreferenceStateWithAvailability,
  matchedPreferencesLabelsDefaults,
  matchedProviderPreferencesLabelsByCountry,
  MEETING_STYLE_MODAL_OPTIONS,
  MEMBER_PREFERENCE_IDS,
  MEMBER_PREFERENCE_NAMES,
  MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING_FOR_MIXPANEL,
  providerPreferenceTypeList,
  ProviderPreferenceValues,
  S4C_PROGRAM_OPTION_FLOW,
} from './constants'
import { genders, lgbtqia, PreferenceMetadataConfig } from './metadata'
import { getAvailableMemberPreferenceListOptions } from './metadata/configuration'
import {
  MemberPreferencesForUserV2,
  UserPreferenceV2ValuesSimplifiedForMixpanel,
  UserPreferenceValuesSimplified,
} from './types'
import {
  ProviderInfo,
  ProviderMemberPreferenceV2,
  ProviderPreferenceMatchV2,
  ProviderPreferenceTypeV2,
} from '../../models/providers/Providers'
import { MEETING_FORMATS } from '../../models/session/Session'
import { DEFAULT_CARE_LANGUAGE } from '../common/constants/constants'
import { PRODUCT_TYPE } from '../common/constants/productType'
import { TREATMENT_OPTIONS, TREATMENT_OPTIONS_TYPE } from '../common/constants/treatmentOptions'
import { CountryNameToISOCode } from '../intl/constants'
import {
  getProviderSupportedPreferenceValues,
  getProviderSupportsInPersonModality,
  getProviderSupportsLiveMessaging,
  getProviderSupportsVideo,
} from '../providers/utils'
import { MEMBER_PREFERENCE_LABELS } from '../searchForCare/messages'
import {
  getShouldShowMemberPreferencesEntryPointSection,
  isTreatmentOptionForAdultTherapy,
} from '../searchForCare/utils'
import meetingStylesPreferenceTherapy from './metadata/meetingSettingTherapy'

export const setMeetingFormatForUpdatedCoachingPreferences = ({
  setMeetingFormatFn,
  updatedValues,
}: {
  setMeetingFormatFn: (value: string) => void
  updatedValues: {
    live_messaging: boolean
    video: boolean
    inPerson?: boolean
  }
}) => {
  if (updatedValues.live_messaging && !updatedValues.video) {
    setMeetingFormatFn(MEETING_FORMATS.LIVE_MESSAGING)
  } else if (updatedValues.video && !updatedValues.live_messaging) {
    setMeetingFormatFn(MEETING_FORMATS.VIDEO)
  } else if (updatedValues.video && updatedValues.live_messaging) {
    setMeetingFormatFn(MEETING_FORMATS.VIDEO_AND_LIVE_MESSAGING)
  } else {
    setMeetingFormatFn(MEETING_FORMATS.NONE_SPECIFIED)
  }
}

export const getVisiblePreferenceCategoriesAsArray = ({
  searchForCareProgramOptionFlow,
  shouldShowAvailabilityPreference,
  shouldExcludeCareLanguageOption = false,
  areExpandedGlobalPreferencesEnabled = false,
  userCountryIsoCode,
}: {
  searchForCareProgramOptionFlow: S4C_PROGRAM_OPTION_FLOW
  shouldShowAvailabilityPreference: boolean
  shouldExcludeCareLanguageOption?: boolean
  areExpandedGlobalPreferencesEnabled?: boolean
  userCountryIsoCode?: string
}) => {
  const availableMemberPreferenceListOptions = getAvailableMemberPreferenceListOptions({
    isUserInternational: !!userCountryIsoCode && userCountryIsoCode !== 'US',
    areExpandedGlobalPreferencesEnabled,
  })
  // PROVREC-4134 : filter out availability in list of visible preferences when feature flag is on
  const filteredPreferences = shouldShowAvailabilityPreference
    ? availableMemberPreferenceListOptions
    : availableMemberPreferenceListOptions.filter((preferenceCategory) => preferenceCategory.name !== 'availabilities')

  const visiblePreferences = filteredPreferences
    .filter((preferenceCategory: PreferenceMetadataConfig) => {
      if (userCountryIsoCode) {
        const preferenceConfigForUserCountry = preferenceCategory.countryConfig[userCountryIsoCode] || null
        if (!preferenceConfigForUserCountry) {
          return false
        }
        return preferenceConfigForUserCountry.programOptionFlows.includes(searchForCareProgramOptionFlow)
      } else {
        return false
      }
    })
    .map((preferenceCategory: PreferenceMetadataConfig) => preferenceCategory.name)

  // Although not shown on the member preferences accordion page, care language preference is treated as a
  // member preference by LW-API / Arbiter and should be included in the list of visiblePreferences, also for data collection
  // Otherwise, exclude this option when just explicity looking for visible preferences
  if (!shouldExcludeCareLanguageOption && shouldDisplayCareLanguagePreference({ searchForCareProgramOptionFlow })) {
    visiblePreferences.push(CARE_LANGUAGES_PREFERENCE_CATEGORY)
  }

  return visiblePreferences
}

// Based on the current treatment option flow, fetch the visible preference categories that the user saw / had the option to select preferences for
export const getVisiblePreferenceCategories = ({
  searchForCareProgramOptionFlow,
  shouldShowAvailabilityPreference,
  shouldExcludeCareLanguageOption = false,
  areExpandedGlobalPreferencesEnabled = false,
  userCountryIsoCode,
}: {
  searchForCareProgramOptionFlow: S4C_PROGRAM_OPTION_FLOW
  shouldShowAvailabilityPreference: boolean
  shouldExcludeCareLanguageOption?: boolean
  areExpandedGlobalPreferencesEnabled?: boolean
  userCountryIsoCode?: string
}) => {
  const visiblePreferences = getVisiblePreferenceCategoriesAsArray({
    searchForCareProgramOptionFlow,
    shouldShowAvailabilityPreference,
    shouldExcludeCareLanguageOption,
    areExpandedGlobalPreferencesEnabled,
    userCountryIsoCode,
  })

  return visiblePreferences.join(',')
}

// This populates `preferenceVariations` in the provider search request and
// is used for data analytics only. It is not used for matching.
export const getPreferenceVariations = ({
  visiblePreferenceCategories,
  userCountryIsoCode,
}: {
  visiblePreferenceCategories: string[]
  userCountryIsoCode?: string
}) => {
  const conservativeIndicatorText = '-conservative'
  const variations = {
    [MEMBER_PREFERENCE_NAMES.GENDER]: () => {
      if (getIsGenderPreferenceConservative({ userCountryIsoCode: userCountryIsoCode ?? '' })) {
        return MEMBER_PREFERENCE_NAMES.GENDER + conservativeIndicatorText
      } else {
        return MEMBER_PREFERENCE_NAMES.GENDER
      }
    },
    [MEMBER_PREFERENCE_NAMES.IS_LGBTQIA]: () => {
      if (getIsLGBTQIAPreferenceConservative({ userCountryIsoCode: userCountryIsoCode ?? '' })) {
        return MEMBER_PREFERENCE_NAMES.IS_LGBTQIA + conservativeIndicatorText
      } else {
        return MEMBER_PREFERENCE_NAMES.IS_LGBTQIA
      }
    },
  }

  const preferenceVariations = visiblePreferenceCategories.map((preferenceCategory) => {
    return variations[preferenceCategory as keyof typeof variations]?.() ?? preferenceCategory
  })

  return preferenceVariations.join(',')
}

export const getNumSelectedPreferenceCategories = ({
  ethnicities = [],
  gender = [],
  isLgbtqia = false,
  religions = [],
  isExperiencedWithVeterans = false,
  preferredAppointmentType = [],
  preferredSessionFormat = [],
  availabilities = null,
  searchForCareProgramOptionFlow,
  isCoachingWithLiveMessagingSearch = false,
  shouldShowAvailabilityPreference = false,
  userCountryIsoCode,
  areExpandedGlobalPreferencesEnabled = false,
}: {
  ethnicities?: string[]
  gender?: string[]
  isLgbtqia?: boolean
  religions?: string[]
  isExperiencedWithVeterans?: boolean
  preferredAppointmentType?: string[]
  preferredSessionFormat?: string[]
  availabilities?: string[] | null
  searchForCareProgramOptionFlow: S4C_PROGRAM_OPTION_FLOW
  isCoachingWithLiveMessagingSearch: boolean
  shouldShowAvailabilityPreference: boolean
  userCountryIsoCode?: string
  areExpandedGlobalPreferencesEnabled?: boolean
}) => {
  const visiblePreferences = getVisiblePreferenceCategoriesAsArray({
    searchForCareProgramOptionFlow,
    shouldShowAvailabilityPreference,
    shouldExcludeCareLanguageOption: true,
    areExpandedGlobalPreferencesEnabled,
    userCountryIsoCode,
  })

  let numSelectedPreferenceCategories = 0
  // Filter out entries to only read meeting setting option
  // Filter out availability option if feature flag is not on
  const preferenceCategories =
    searchForCareProgramOptionFlow === S4C_PROGRAM_OPTION_FLOW.PARENT_INITIATED_TEEN ||
    searchForCareProgramOptionFlow === S4C_PROGRAM_OPTION_FLOW.PARENT_INITIATED_CHILD ||
    searchForCareProgramOptionFlow === S4C_PROGRAM_OPTION_FLOW.SELF_INITIATED_TEENS
      ? Object.entries({
          preferredAppointmentType,
        })
      : Object.entries({
          ethnicities,
          gender,
          isLgbtqia,
          religions,
          isExperiencedWithVeterans,
          preferredAppointmentType,
          preferredSessionFormat,
          ...(shouldShowAvailabilityPreference && { availabilities }),
        })

  const isValidPreferredAppointmentTypeOption = getIsValidSearchForModalityPreferenceForMemberPreferences({
    searchForCareProgramOptionFlow,
    userCountryIsoCode,
  })

  // Check if user has selected any options through the list of preference categories
  preferenceCategories.forEach(([key, value]) => {
    switch (key) {
      case 'availabilities':
        if (
          visiblePreferences.includes(MEMBER_PREFERENCE_NAMES.AVAILABILITIES) &&
          !isEmptyAvailabilityPreferenceOption({ availabilities })
        ) {
          numSelectedPreferenceCategories++
        }
        break
      case 'preferredAppointmentType':
        if (
          visiblePreferences.includes(MEMBER_PREFERENCE_NAMES.MEETING_SETTING) &&
          isValidPreferredAppointmentTypeOption &&
          isArray(value) &&
          value.length
        ) {
          numSelectedPreferenceCategories++
        }
        break
      case 'preferredSessionFormat':
        if (
          visiblePreferences.includes(MEMBER_PREFERENCE_NAMES.MEETING_SETTING) &&
          isCoachingWithLiveMessagingSearch &&
          isArray(value) &&
          value.length
        ) {
          numSelectedPreferenceCategories++
        }
        break
      case 'ethnicities':
        if (visiblePreferences.includes(MEMBER_PREFERENCE_NAMES.ETHNICITIES) && isArray(value) && value.length) {
          numSelectedPreferenceCategories++
        }
        break
      case 'gender':
        if (visiblePreferences.includes(MEMBER_PREFERENCE_NAMES.GENDER) && isArray(value) && value.length) {
          numSelectedPreferenceCategories++
        }
        break
      case 'religions':
        if (visiblePreferences.includes(MEMBER_PREFERENCE_NAMES.RELIGIONS) && isArray(value) && value.length) {
          numSelectedPreferenceCategories++
        }
        break
      case 'isLgbtqia':
        if (visiblePreferences.includes(MEMBER_PREFERENCE_NAMES.IS_LGBTQIA) && value) {
          numSelectedPreferenceCategories++
        }
        break
      case 'isExperiencedWithVeterans':
        if (visiblePreferences.includes(MEMBER_PREFERENCE_NAMES.IS_EXPERIENCED_WITH_VETERANS) && value) {
          numSelectedPreferenceCategories++
        }
        break
      default:
        break
    }
  })
  // Return total number of preference categories, relevant to the current treatment option flow
  return numSelectedPreferenceCategories
}

export const getMatchedPreferences = ({
  provider,
  userMemberPreferences,
  searchForCareProgramOptionFlow,
  userCountryIsoCode,
}: {
  provider: ProviderInfo | undefined
  userMemberPreferences: MemberPreferencesForUserV2 | null
  searchForCareProgramOptionFlow: S4C_PROGRAM_OPTION_FLOW
  userCountryIsoCode: string
}) => {
  const initialArr: MessageDescriptor[] = []

  // Sort the preference types in order of the provider preference selection page
  const sortedMemberPreferences = cloneDeep(provider?.memberPreferences || []).sort(
    (a, b) =>
      providerPreferenceTypeList.indexOf(a.type as ProviderPreferenceTypeV2) -
      providerPreferenceTypeList.indexOf(b.type as ProviderPreferenceTypeV2),
  )

  const matchedProviderPreferenceLabelV2 = userCountryIsoCode
    ? matchedProviderPreferencesLabelsByCountry[userCountryIsoCode] ?? matchedPreferencesLabelsDefaults
    : matchedPreferencesLabelsDefaults

  const isInternationalUser = userCountryIsoCode !== CountryNameToISOCode.UnitedStatesOfAmerica
  return (
    sortedMemberPreferences?.reduce((arr, memberPreference) => {
      if (memberPreference.match == ProviderPreferenceMatchV2.FULL) {
        // Member preferences V2 uses GRANULAR_ETHNICITY instead of ETHNICITY here since
        // the former utilizes the multi-select structure
        if (memberPreference.type == ProviderPreferenceTypeV2.ETHNICITY) {
          return arr
        }
        if (memberPreference.type == ProviderPreferenceTypeV2.SESSION_MODALITY) {
          const label = []
          if (
            searchForCareProgramOptionFlow === S4C_PROGRAM_OPTION_FLOW.THERAPY ||
            searchForCareProgramOptionFlow === S4C_PROGRAM_OPTION_FLOW.PARENT_INITIATED_CHILD ||
            searchForCareProgramOptionFlow === S4C_PROGRAM_OPTION_FLOW.PARENT_INITIATED_TEEN
          ) {
            // Therapy has the possibility of only in-person matches
            if (
              provider &&
              getProviderSupportsInPersonModality({ provider }) &&
              userMemberPreferences?.preferredAppointmentType.includes(MEETING_FORMATS.IN_PERSON)
            ) {
              label.push(matchedProviderPreferenceLabelV2[memberPreference.type].in_person)
            }
          } else {
            // Coaching has the possibility of video or live messaging matches
            if (
              provider &&
              getProviderSupportsVideo({ provider }) &&
              userMemberPreferences?.preferredSessionFormat.includes(MEETING_FORMATS.VIDEO)
            ) {
              label.push(matchedProviderPreferenceLabelV2[memberPreference.type].video)
            }
            if (
              provider &&
              getProviderSupportsLiveMessaging({ provider }) &&
              userMemberPreferences?.preferredSessionFormat.includes(MEETING_FORMATS.LIVE_MESSAGING)
            ) {
              label.push(matchedProviderPreferenceLabelV2[memberPreference.type].live_messaging)
            }
          }
          return [...arr, ...label]
        } else if (memberPreference.type == ProviderPreferenceTypeV2.SEXUAL_ORIENTATION) {
          return [...arr, matchedProviderPreferenceLabelV2[memberPreference.type].LGBTQIA_MATCH_IDENTITY]
        } else if (memberPreference.type == ProviderPreferenceTypeV2.EXPERIENCE_RELIGION) {
          // Match any Religion options the user and provider have both selected
          const labels: MessageDescriptor[] = []
          const matches = getProviderSupportedPreferenceValues(
            provider?.religions || [],
            userMemberPreferences?.religions || [],
          )
          matches.forEach((preference) =>
            labels.push(
              (
                matchedProviderPreferenceLabelV2[
                  memberPreference.type as keyof typeof matchedProviderPreferenceLabelV2
                ] as Record<string, MessageDescriptor>
              )[preference],
            ),
          )
          return [...arr, ...labels]
        } else if (memberPreference.type == ProviderPreferenceTypeV2.GRANULAR_ETHNICITY) {
          // Match any Ethnicity options the user and provider have both selected
          const labels: MessageDescriptor[] = []
          // PROSPECT-3796: Filter out MENA option if user is NOT searching for therapy
          const filteredUserRaceEthnicityMemberPreference = filterMemberPreferencesOptions({
            preferenceCategory: userMemberPreferences?.ethnicities || [],
            shouldFilterMemberPreferencesOptions: searchForCareProgramOptionFlow !== S4C_PROGRAM_OPTION_FLOW.THERAPY,
            filterOptions: [MEMBER_PREFERENCE_IDS.MIDDLE_EASTERN_OR_NORTH_AFRICAN],
          })

          const provider_ethnicity = isInternationalUser
            ? provider?.intl_provider_ethnicity
            : provider?.provider_ethnicity
          const matches = getProviderSupportedPreferenceValues(
            provider_ethnicity || [],
            filteredUserRaceEthnicityMemberPreference,
          )
          matches.forEach((preference) =>
            labels.push(
              (
                matchedProviderPreferenceLabelV2[
                  memberPreference.type as keyof typeof matchedProviderPreferenceLabelV2
                ] as Record<string, MessageDescriptor>
              )[preference] || (MEMBER_PREFERENCE_LABELS as Record<string, MessageDescriptor>)[preference],
            ),
          )
          return [...arr, ...labels]
        } else if (memberPreference.type == ProviderPreferenceTypeV2.GENDER) {
          // Match any Gender options the user and provider have both selected
          const labels: MessageDescriptor[] = []
          const matches = getProviderSupportedPreferenceValues(
            provider?.genders || [],
            userMemberPreferences?.gender || [],
          )
          matches.forEach((preference) =>
            labels.push((MEMBER_PREFERENCE_LABELS as Record<string, MessageDescriptor>)[preference]),
          )
          return [...arr, ...labels]
        } else if (memberPreference.type == ProviderPreferenceTypeV2.AVAILABILITY) {
          if (!isEmptyAvailabilityPreferenceOption({ availabilities: userMemberPreferences?.availabilities })) {
            // For all valid preference options, we will determine which copy to use by the length of slots the users has selected
            const availabilityPreference = userMemberPreferences?.availabilities
            if (availabilityPreference && availabilityPreference.length > 1) {
              return [...arr, matchedProviderPreferenceLabelV2[memberPreference.type].SPECIFIC_DAYS_AND_TIMES]
            } else {
              return [...arr, matchedProviderPreferenceLabelV2[memberPreference.type].SPECIFIC_DAY_AND_TIME]
            }
          } else {
            return arr
          }
        }
        return [...arr, matchedProviderPreferenceLabelV2[memberPreference.type]]
      } else if (memberPreference.match == ProviderPreferenceMatchV2.PARTIAL) {
        // LGBTQIA+ and Religion have partial options
        if (memberPreference.type == ProviderPreferenceTypeV2.SEXUAL_ORIENTATION) {
          return [...arr, matchedProviderPreferenceLabelV2[memberPreference.type].LGBTQIA_MATCH_EXPERIENCE]
        } else if (memberPreference.type == ProviderPreferenceTypeV2.EXPERIENCE_RELIGION) {
          return [
            ...arr,
            matchedProviderPreferenceLabelV2[memberPreference.type][MEMBER_PREFERENCE_IDS.CLIENTS_OF_FAITH],
          ]
        }
        return arr
      } else {
        return arr
      }
    }, initialArr) || initialArr
  )
}

// Determines whether or not least one provider in the list of provider results, fulfills a preference specified by the user
export const doesProviderResultsFulfillMemberPreferences = ({ providers }: { providers: ProviderInfo[] }) => {
  return providers.some((provider: ProviderInfo) => {
    const providerMemberPreferences = provider?.memberPreferences as ProviderMemberPreferenceV2[] | undefined
    return providerMemberPreferences?.some((preference: ProviderMemberPreferenceV2) => {
      // Ignore "ETHNICITY" type matches -- "ETHNICITY" is a v1 preference category that remains in the obj
      // for backwards compatibility. "GRANULAR_ETHNICITY" represents the desired match for v2
      if (preference.match === ProviderPreferenceMatchV2.FULL && preference.type !== ProviderPreferenceTypeV2.ETHNICITY)
        return true
      else if (
        // Fallback preferences, i.e. "Experience with clients of faith" or "Experience with LGBTQIA+ clients"
        preference.match === ProviderPreferenceMatchV2.PARTIAL &&
        [ProviderPreferenceTypeV2.SEXUAL_ORIENTATION, ProviderPreferenceTypeV2.EXPERIENCE_RELIGION].includes(
          preference.type,
        )
      ) {
        return true
      }
      return false
    })
  })
}

// With the introduction of availability preference, we are not comparing the timeZone property since that
// value is not determine/touched by the user. Aditionally, for availability preference we need to check
// if the value is in an "empty" state as well
export const isMemberPreferencesEmpty = ({
  memberPreferences,
  shouldShowAvailabilityPreference,
  isChildSearchForCare,
}: {
  memberPreferences: MemberPreferencesForUserV2 | null
  shouldShowAvailabilityPreference: boolean
  isChildSearchForCare: boolean
}) => {
  if (!memberPreferences) return true
  // V3 flow
  if (shouldShowAvailabilityPreference) {
    // Child searches don't show non-INP and availability preferences, so filter them out if so
    const { preferredAppointmentType, timeZone, availabilities } = memberPreferences
    const validPreferences = isChildSearchForCare
      ? {
          ...defaultV3PreferencesForUser,
          preferredAppointmentType,
          timeZone,
          availabilities,
        }
      : memberPreferences

    const memberPreferencesWithoutAvailabilityTimezone = omit(validPreferences, ['timeZone', 'availabilities'])

    // Preferences shape without availability and timezone is just v2
    const defaultPreferencesWithoutAvailabilityTimezone = defaultV2PreferencesForUser
    return (
      isEqual(memberPreferencesWithoutAvailabilityTimezone, defaultPreferencesWithoutAvailabilityTimezone) &&
      isEmptyAvailabilityPreferenceOption({ availabilities: validPreferences?.availabilities })
    )
  } else {
    // Child searches don't show non-INP preferences, so filter them out if so
    const { preferredAppointmentType } = memberPreferences
    const validPreferences = isChildSearchForCare
      ? {
          ...defaultV2PreferencesForUser,
          preferredAppointmentType,
        }
      : memberPreferences
    return isEqual(validPreferences, defaultV2PreferencesForUser)
  }
}

// Returns true if the user did not specifiy ANY preference when searching for providers. The logic for this check varies based on treatment option
export const isMemberPreferencesUnspecifiedByUser = ({
  memberPreferences,
  selectedTreatmentOption,
  selectedCareLanguage,
  isUserInternational,
  shouldShowAvailabilityPreference = false,
  isChildSearchForCare,
  areMemberPreferencesApplicableForSearchForCareFlow,
}: {
  memberPreferences: MemberPreferencesForUserV2 | null
  selectedTreatmentOption: string
  selectedCareLanguage: string | null
  isUserInternational: boolean
  shouldShowAvailabilityPreference?: boolean
  isChildSearchForCare: boolean
  areMemberPreferencesApplicableForSearchForCareFlow: boolean
}) => {
  // We only consider care language a preference when its specified as domestic user + non-English, or international user
  const shouldConsiderCareLanguageAsPreference = isCareLanguageValidPreference({
    isSearchingForAdultTherapy: isTreatmentOptionForAdultTherapy(selectedTreatmentOption as TREATMENT_OPTIONS_TYPE),
    isUserInternational,
    selectedCareLanguage,
    areMemberPreferencesApplicableForSearchForCareFlow,
  })
  const languagePreference = shouldConsiderCareLanguageAsPreference ? selectedCareLanguage : null
  const isUserWithPreferences =
    (!isUserInternational || areMemberPreferencesApplicableForSearchForCareFlow) && memberPreferences

  if (languagePreference) return false

  // Member preferences V2 contains a different set of values that will check all six category values
  if (isUserWithPreferences) {
    return isMemberPreferencesEmpty({ isChildSearchForCare, memberPreferences, shouldShowAvailabilityPreference })
  }
  return true
}

// The matched preferences header should show if either no preferences were specified by the user, or if at least one preference was fulfilled
// within the list of returned recommended providers
export const shouldShowMemberPreferencesMatchedHeader = ({
  memberPreferences,
  selectedTreatmentOption,
  providers,
  selectedCareLanguage,
  isUserInternational,
  shouldShowAvailabilityPreference,
  isChildSearchForCare,
  areMemberPreferencesApplicableForSearchForCareFlow,
}: {
  memberPreferences: MemberPreferencesForUserV2 | null
  selectedTreatmentOption: string
  providers: ProviderInfo[]
  selectedCareLanguage: string | null
  isUserInternational: boolean
  shouldShowAvailabilityPreference: boolean
  isChildSearchForCare: boolean
  areMemberPreferencesApplicableForSearchForCareFlow: boolean
}) => {
  return (
    isMemberPreferencesUnspecifiedByUser({
      memberPreferences,
      selectedTreatmentOption,
      selectedCareLanguage,
      isUserInternational,
      shouldShowAvailabilityPreference,
      isChildSearchForCare,
      areMemberPreferencesApplicableForSearchForCareFlow,
    }) || doesProviderResultsFulfillMemberPreferences({ providers })
  )
}

// This is used for determining the selected preference options for ui functionality. For example,
// this is used to determine the total number of options selected to display an "x of y preferences matched"
// label on the provider results cards.
// For mixpanel tracking, use `getSelectedPreferenceOptionsForMixpanel` instead.
export const getSelectedPreferenceOptions = ({
  userMemberPreferences,
  eligibleTreatmentOptions,
  isTherapySearch,
  isSearchingForCoaching,
  isOnsiteLocation,
  shouldShowAvailabilityPreference,
  areMemberPreferencesApplicableForSearchForCareFlow,
  visiblePreferences,
}: {
  userMemberPreferences: MemberPreferencesForUserV2 | null
  eligibleTreatmentOptions?: TREATMENT_OPTIONS_TYPE[]
  isTherapySearch: boolean
  isSearchingForCoaching: boolean
  isOnsiteLocation: boolean
  shouldShowAvailabilityPreference: boolean
  areMemberPreferencesApplicableForSearchForCareFlow: boolean
  visiblePreferences: string[]
}): Partial<Record<MEMBER_PREFERENCE_NAMES, any>> => {
  if (!userMemberPreferences || !areMemberPreferencesApplicableForSearchForCareFlow || isOnsiteLocation) return {}

  const isCoachingWithLiveMessagingSearch =
    isSearchingForCoaching &&
    (eligibleTreatmentOptions ? eligibleTreatmentOptions.includes(TREATMENT_OPTIONS.LIVE_MESSAGING_COACHING) : false)

  const {
    ethnicities,
    gender,
    isLgbtqia,
    religions,
    isExperiencedWithVeterans,
    preferredAppointmentType,
    preferredSessionFormat,
    availabilities,
  } = userMemberPreferences

  const selectedPreferences: Record<string, any> = {}
  if (visiblePreferences.includes(MEMBER_PREFERENCE_NAMES.ETHNICITIES)) {
    selectedPreferences[MEMBER_PREFERENCE_NAMES.ETHNICITIES] = ethnicities
  }
  if (visiblePreferences.includes(MEMBER_PREFERENCE_NAMES.GENDER)) {
    selectedPreferences[MEMBER_PREFERENCE_NAMES.GENDER] = gender
  }
  if (visiblePreferences.includes(MEMBER_PREFERENCE_NAMES.IS_LGBTQIA)) {
    selectedPreferences[MEMBER_PREFERENCE_NAMES.IS_LGBTQIA] = isLgbtqia
  }
  if (visiblePreferences.includes(MEMBER_PREFERENCE_NAMES.RELIGIONS)) {
    selectedPreferences[MEMBER_PREFERENCE_NAMES.RELIGIONS] = religions
  }
  if (visiblePreferences.includes(MEMBER_PREFERENCE_NAMES.IS_EXPERIENCED_WITH_VETERANS)) {
    selectedPreferences[MEMBER_PREFERENCE_NAMES.IS_EXPERIENCED_WITH_VETERANS] = isExperiencedWithVeterans
  }
  if (visiblePreferences.includes(MEMBER_PREFERENCE_NAMES.MEETING_SETTING) && isTherapySearch) {
    selectedPreferences[MEMBER_PREFERENCE_NAMES.MEETING_SETTING] = preferredAppointmentType
  }
  if (visiblePreferences.includes(MEMBER_PREFERENCE_NAMES.MEETING_SETTING) && isCoachingWithLiveMessagingSearch) {
    selectedPreferences[MEMBER_PREFERENCE_NAMES.MEETING_SETTING] = preferredSessionFormat
  }
  if (visiblePreferences.includes(MEMBER_PREFERENCE_NAMES.AVAILABILITIES) && shouldShowAvailabilityPreference) {
    // Map inputted availabilities into readable format
    const parsedAvailabilities =
      availabilities?.length && !availabilities.includes('flexible')
        ? convertAvailabilitiesDayOfWeek(availabilities)
        : availabilities
    selectedPreferences[MEMBER_PREFERENCE_NAMES.AVAILABILITIES] = parsedAvailabilities
  }

  return selectedPreferences
}

// This is used for populating the preference_detail property on mixpanel events.
// This returns an object containing the list of selected preferences relevant to the
// current treatment option and indicates conservative vs. progressive preference categories.
// For ui functionality, use `getSelectedPreferenceOptions` instead.
export const getSelectedPreferenceOptionsForMixpanel = ({
  userMemberPreferences,
  eligibleTreatmentOptions,
  isTherapySearch,
  isSearchingForCoaching,
  isOnsiteLocation,
  shouldShowAvailabilityPreference,
  areMemberPreferencesApplicableForSearchForCareFlow,
  visiblePreferences,
  userCountryIsoCode,
}: {
  userMemberPreferences: MemberPreferencesForUserV2 | null
  eligibleTreatmentOptions?: TREATMENT_OPTIONS_TYPE[]
  isTherapySearch: boolean
  isSearchingForCoaching: boolean
  isOnsiteLocation: boolean
  shouldShowAvailabilityPreference: boolean
  areMemberPreferencesApplicableForSearchForCareFlow: boolean
  visiblePreferences: string[]
  userCountryIsoCode: string
}): UserPreferenceV2ValuesSimplifiedForMixpanel => {
  if (!userMemberPreferences || !areMemberPreferencesApplicableForSearchForCareFlow || isOnsiteLocation) return {}

  const isCoachingWithLiveMessagingSearch =
    isSearchingForCoaching &&
    (eligibleTreatmentOptions ? eligibleTreatmentOptions.includes(TREATMENT_OPTIONS.LIVE_MESSAGING_COACHING) : false)

  const {
    ethnicities,
    gender,
    isLgbtqia,
    religions,
    isExperiencedWithVeterans,
    preferredAppointmentType,
    preferredSessionFormat,
    availabilities,
  } = userMemberPreferences

  const selectedPreferences: Record<string, any> = {}
  if (visiblePreferences.includes(MEMBER_PREFERENCE_NAMES.ETHNICITIES)) {
    selectedPreferences[MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING_FOR_MIXPANEL.ethnicities] = ethnicities
  }
  if (visiblePreferences.includes(MEMBER_PREFERENCE_NAMES.GENDER)) {
    if (getIsGenderPreferenceConservative({ userCountryIsoCode })) {
      selectedPreferences[MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING_FOR_MIXPANEL.genderConservative] = gender
    } else {
      selectedPreferences[MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING_FOR_MIXPANEL.gender] = gender
    }
  }
  if (visiblePreferences.includes(MEMBER_PREFERENCE_NAMES.IS_LGBTQIA)) {
    if (getIsLGBTQIAPreferenceConservative({ userCountryIsoCode })) {
      selectedPreferences[MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING_FOR_MIXPANEL.isLgbtqiaConservative] = isLgbtqia
    } else {
      selectedPreferences[MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING_FOR_MIXPANEL.isLgbtqia] = isLgbtqia
    }
  }
  if (visiblePreferences.includes(MEMBER_PREFERENCE_NAMES.RELIGIONS)) {
    selectedPreferences[MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING_FOR_MIXPANEL.religions] = religions
  }
  if (visiblePreferences.includes(MEMBER_PREFERENCE_NAMES.IS_EXPERIENCED_WITH_VETERANS)) {
    selectedPreferences[MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING_FOR_MIXPANEL.isExperiencedWithVeterans] =
      isExperiencedWithVeterans
  }
  if (visiblePreferences.includes(MEMBER_PREFERENCE_NAMES.MEETING_SETTING) && isTherapySearch) {
    selectedPreferences[MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING_FOR_MIXPANEL.meetingSetting] =
      preferredAppointmentType
  }
  if (visiblePreferences.includes(MEMBER_PREFERENCE_NAMES.MEETING_SETTING) && isCoachingWithLiveMessagingSearch) {
    selectedPreferences[MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING_FOR_MIXPANEL.meetingSetting] =
      preferredSessionFormat
  }
  if (visiblePreferences.includes(MEMBER_PREFERENCE_NAMES.AVAILABILITIES) && shouldShowAvailabilityPreference) {
    // Map inputted availabilities into readable format
    const parsedAvailabilities =
      availabilities?.length && !availabilities.includes('flexible')
        ? convertAvailabilitiesDayOfWeek(availabilities)
        : availabilities
    selectedPreferences[MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING_FOR_MIXPANEL.availabilities] =
      parsedAvailabilities
  }

  return selectedPreferences
}

// Use this function for member preferences in search request payload
export const getUserModalityPreferenceForMemberPreferences = ({
  userMemberPreferences,
  isValidSearchForModalityPreferenceForMemberPreferences,
}: {
  isValidSearchForModalityPreferenceForMemberPreferences: boolean
  userMemberPreferences: MemberPreferencesForUserV2 | null
}) => {
  if (!isValidSearchForModalityPreferenceForMemberPreferences) return '[]'
  if (!userMemberPreferences?.preferredAppointmentType || !userMemberPreferences?.preferredAppointmentType.length)
    return MEETING_FORMATS.NONE_SPECIFIED
  return userMemberPreferences?.preferredAppointmentType.join(',')
}

export const isCareLanguageValidPreference = ({
  isSearchingForAdultTherapy,
  isUserInternational,
  selectedCareLanguage,
  areMemberPreferencesApplicableForSearchForCareFlow,
}: {
  isSearchingForAdultTherapy: boolean
  isUserInternational: boolean
  selectedCareLanguage: string | null
  areMemberPreferencesApplicableForSearchForCareFlow?: boolean
}) => {
  return (
    isSearchingForAdultTherapy &&
    !!selectedCareLanguage &&
    ((isUserInternational && !areMemberPreferencesApplicableForSearchForCareFlow) ||
      selectedCareLanguage !== DEFAULT_CARE_LANGUAGE)
  )
}

const matchedMeetingSettingFormatValues = {
  [MEMBER_PREFERENCE_IDS.HIGHLIGHT_PROVIDERS_WHO_OFFER_IN_PERSON_SESSIONS]: MEETING_FORMATS.IN_PERSON,
  [MEMBER_PREFERENCE_IDS.LIVE_MESSAGING]: MEETING_FORMATS.LIVE_MESSAGING,
  [MEMBER_PREFERENCE_IDS.VIDEO]: MEETING_FORMATS.VIDEO,
}

/// Returns provider preference selection checkbox ID values to meeting format values
export const matchMeetingSettingFormatToMemberPreferencesValues = ({
  meetingSetting,
}: {
  meetingSetting: string[]
}) => {
  return meetingSetting.map((name) => (matchedMeetingSettingFormatValues as Record<string, MEETING_FORMATS>)[name])
}

// Returns saved member preference values to provider preference selection checkbox ID values
export const matchMemberPreferencesValuesToMeetingSettingFormat = ({
  preferenceValues,
}: {
  preferenceValues: string[]
}) => {
  return preferenceValues.map(
    (value) =>
      Object.keys(matchedMeetingSettingFormatValues).find(
        (key) => (matchedMeetingSettingFormatValues as Record<string, MEETING_FORMATS>)[key] === value,
      ) as string,
  )
}

// Returns true if the user has made any selections under Race/Ethnicty section
// If the user only selects White, then it is not classified as BIPOC
export const getBipocUserPreferenceSelection = ({
  userPreferenceSelections,
}: {
  userPreferenceSelections: string[]
}) => {
  return userPreferenceSelections.length !== 0 && !isEqual(userPreferenceSelections, [MEMBER_PREFERENCE_IDS.WHITE])
}

// Business logic for determining which "Meeting Style" modal should be shown to the user. We currently have
// two formats with showing the in-person option and showing video/live messaging options.
export const getModalityPreferenceProgramOption = ({
  shouldShowMeetingStylePreferenceModal,
  isSelectedTreatmentOptionCoaching,
}: {
  shouldShowMeetingStylePreferenceModal: boolean
  isSelectedTreatmentOptionCoaching: boolean
}) => {
  if (!shouldShowMeetingStylePreferenceModal) {
    return MEETING_STYLE_MODAL_OPTIONS.NONE
  }
  return isSelectedTreatmentOptionCoaching
    ? MEETING_STYLE_MODAL_OPTIONS.VIDEO_AND_LIVE_MESSAGING
    : MEETING_STYLE_MODAL_OPTIONS.IN_PERSON
}

/**
 * Function that returns preference categories shown to the user under the provider preference selection page
 * based on the current search for care flow of the user
 */
export const getMemberPreferencesShown = ({
  searchForCareProgramOptionFlow,
  shouldShowAvailabilityPreference,
  userCountryIsoCode,
  areExpandedGlobalPreferencesEnabled,
}: {
  searchForCareProgramOptionFlow: S4C_PROGRAM_OPTION_FLOW
  shouldShowAvailabilityPreference: boolean
  userCountryIsoCode: string
  areExpandedGlobalPreferencesEnabled?: boolean
}): string[] => {
  const visiblePreferencesCategories = getVisiblePreferenceCategoriesAsArray({
    searchForCareProgramOptionFlow,
    shouldShowAvailabilityPreference,
    shouldExcludeCareLanguageOption: true,
    areExpandedGlobalPreferencesEnabled,
    userCountryIsoCode,
  })

  return visiblePreferencesCategories.map((value) => {
    if (
      value === MEMBER_PREFERENCE_NAMES.GENDER &&
      getIsGenderPreferenceConservative({
        userCountryIsoCode,
      })
    ) {
      return MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING_FOR_MIXPANEL.genderConservative
    } else if (
      value === MEMBER_PREFERENCE_NAMES.IS_LGBTQIA &&
      getIsLGBTQIAPreferenceConservative({
        userCountryIsoCode,
      })
    ) {
      return MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING_FOR_MIXPANEL.isLgbtqiaConservative
    } else {
      return MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING_FOR_MIXPANEL[
        value as keyof typeof MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING_FOR_MIXPANEL
      ]
    }
  })
}

// Set default empty string arrays if preferences are empty. Otherwise, translate object
// into string arrays for checkbox values
export const getInitialProviderPreferenceValues = ({
  memberPreferencesV2,
  isValidSearchForModalityPreferenceForMemberPreferences,
  isCoachingWithLiveMessagingSearch,
  shouldShowAvailabilityPreference = false,
}: {
  memberPreferencesV2: MemberPreferencesForUserV2 | null
  isValidSearchForModalityPreferenceForMemberPreferences: boolean
  isCoachingWithLiveMessagingSearch: boolean
  shouldShowAvailabilityPreference?: boolean
}): ProviderPreferenceValues => {
  if (isEmpty(memberPreferencesV2)) {
    // Only add availability properties when feature flag is on
    if (shouldShowAvailabilityPreference) {
      return cloneDeep(emptyProviderPreferenceStateWithAvailability)
    } else {
      return cloneDeep(emptyProviderPreferenceState)
    }
  }

  // PROSPECT-3928: Temp logic - we'll need to filter out the Mormon option on the FE side for now
  // if the user has saved the option in their member preferences
  const filteredReligionPreference = filterMemberPreferencesOptions({
    preferenceCategory: memberPreferencesV2?.religions || [],
    shouldFilterMemberPreferencesOptions: true, // filter out option for all flows
    filterOptions: [MEMBER_PREFERENCE_IDS.MORMON],
  })

  return {
    ethnicities: memberPreferencesV2.ethnicities,
    gender: memberPreferencesV2.gender,
    isLgbtqia: memberPreferencesV2.isLgbtqia ? [MEMBER_PREFERENCE_IDS['LGBTQIA+']] : [],
    religions: filteredReligionPreference,
    isExperiencedWithVeterans: memberPreferencesV2.isExperiencedWithVeterans
      ? [MEMBER_PREFERENCE_IDS.EXPERIENCE_WORKING_WITH_VETERANS]
      : [],
    meetingSetting: isValidSearchForModalityPreferenceForMemberPreferences
      ? matchMemberPreferencesValuesToMeetingSettingFormat({
          preferenceValues: memberPreferencesV2.preferredAppointmentType,
        })
      : isCoachingWithLiveMessagingSearch
      ? matchMemberPreferencesValuesToMeetingSettingFormat({
          preferenceValues: memberPreferencesV2.preferredSessionFormat,
        })
      : [],
    ...(shouldShowAvailabilityPreference && {
      availabilities: memberPreferencesV2.availabilities,
    }),
  }
}

/** Function that filters member preference category options based on the type of treatment option the user selected */
export const filterMemberPreferencesOptions = ({
  preferenceCategory,
  shouldFilterMemberPreferencesOptions,
  filterOptions,
}: {
  preferenceCategory: string[]
  shouldFilterMemberPreferencesOptions: boolean
  filterOptions: string[]
}): string[] => {
  return shouldFilterMemberPreferencesOptions
    ? preferenceCategory.filter((ethnicity: string) => !filterOptions.includes(ethnicity))
    : preferenceCategory
}

/**
 * Returns check if using preferredAppointmentType value under member preferences
 */
export const getIsValidSearchForModalityPreferenceForMemberPreferences = ({
  searchForCareProgramOptionFlow,
  userCountryIsoCode,
}: {
  searchForCareProgramOptionFlow: S4C_PROGRAM_OPTION_FLOW
  userCountryIsoCode?: string
}): boolean => {
  if (userCountryIsoCode) {
    const preferenceConfigForUserCountry = meetingStylesPreferenceTherapy.countryConfig[userCountryIsoCode] || null
    if (!preferenceConfigForUserCountry) {
      return false
    }
    return preferenceConfigForUserCountry.programOptionFlows.includes(searchForCareProgramOptionFlow)
  }
  return false
}

export const getUserSelectedPreferencesCountTotal = ({
  userSelectedPreferences,
  selectedCareLanguage,
}: {
  userSelectedPreferences: UserPreferenceValuesSimplified | null
  selectedCareLanguage: string | null
}) => {
  let countTotal = 0
  if (selectedCareLanguage && selectedCareLanguage !== DEFAULT_CARE_LANGUAGE) countTotal++
  if (!userSelectedPreferences || isEmpty(userSelectedPreferences)) return countTotal
  Object.entries(userSelectedPreferences).forEach(([prefKey, prefValue]) => {
    if (typeof prefValue === 'boolean') {
      if (prefValue !== false) {
        countTotal++
      }
    } else if (typeof prefValue === 'object' && Array.isArray(prefValue)) {
      if (prefValue.length > 0) {
        if (prefKey === MEMBER_PREFERENCE_NAMES.AVAILABILITIES) {
          if (!prefValue.includes(MEMBER_PREFERENCE_IDS.FLEXIBLE)) countTotal++
        } else countTotal += prefValue.length
      }
    }
  })
  return countTotal
}

export const shouldDisplayCareLanguagePreference = ({
  searchForCareProgramOptionFlow,
}: {
  searchForCareProgramOptionFlow: S4C_PROGRAM_OPTION_FLOW
}): boolean => {
  return searchForCareProgramOptionFlow === S4C_PROGRAM_OPTION_FLOW.THERAPY
}

/**
 * Function that returns a list of all the provider's matching availabilities from Arbiter
 * and if provider has a MatchLevel of FULL
 */
export const getAvailabilityPreferenceMatchLevelAndSlots = ({
  provider,
  shouldShowAvailabilityPreference,
}: {
  provider: ProviderInfo | undefined
  shouldShowAvailabilityPreference: boolean | undefined
}) => {
  if (!provider || !shouldShowAvailabilityPreference)
    return {
      hasFullAvailabilityMatch: false,
      matchingScheduleSlotsString: [],
    }
  else {
    const availabilityMatchLevel = provider.memberPreferences?.find(
      (matchLevel) => matchLevel.type == ProviderPreferenceTypeV2.AVAILABILITY,
    )
    return {
      hasFullAvailabilityMatch: availabilityMatchLevel?.match == ProviderPreferenceMatchV2.FULL || false,
      matchingScheduleSlotsString:
        availabilityMatchLevel && availabilityMatchLevel.matchingScheduleSlotsString
          ? availabilityMatchLevel.matchingScheduleSlotsString
          : [],
    }
  }
}

// If there is at least one datetime that matches the user's availability preference, display that as a bookable appointment to the user
// Otherwise, display the first available appointment as a bookable appointment to the user
export const getProviderFirstAvailabilityDate = ({
  shouldShowAvailabilityPreference = false,
  hasFullAvailabilityMatch,
  matchingScheduleSlotsString,
  providerFirstAvailability,
}: {
  shouldShowAvailabilityPreference: boolean | undefined
  hasFullAvailabilityMatch: boolean
  matchingScheduleSlotsString: string[]
  providerFirstAvailability: Date | undefined
}) => {
  const firstMatchingScheduleAvailability =
    matchingScheduleSlotsString.length > 0 ? new Date(matchingScheduleSlotsString[0]) : undefined
  return shouldShowAvailabilityPreference && hasFullAvailabilityMatch
    ? firstMatchingScheduleAvailability
    : providerFirstAvailability
}

export const convertAvailabilitiesDayOfWeek = (availabilities: string[]) => {
  return availabilities.map((key) => {
    const availabilitySplit = key.split('-')
    const dayOfWeek = availabilitySplit[0] as unknown as number
    const timeOfDay = availabilitySplit[1] as unknown as number
    return `${AVAILABILITY_PREFERENCE_DAY_OF_WEEK_MAPPING[dayOfWeek]}-${AVAILABILITY_PREFERENCE_TIME_OF_DAY_MAPPING[timeOfDay]}`
  })
}

/** Checks if the availability preference is in an "empty" state, which includes:
 *  - The user has not engaged in availability
 *  - The user has selected "I'm flexible option"
 *  - The user has selected "Specify date and time" with no time slots selected
 */
export const isEmptyAvailabilityPreferenceOption = ({
  availabilities,
}: {
  availabilities?: string[] | null
}): boolean => {
  return availabilities == null || availabilities.length == 0 || availabilities.includes('flexible')
}

// Checks if at least one provider has a FULL availability match from provider results
export const hasAvailabilityProviderMatch = ({ providers }: { providers: ProviderInfo[] | undefined }) => {
  return providers?.some((provider) => {
    const availabilityMatchLevel = provider.memberPreferences?.find(
      (matchLevel) => matchLevel.type == ProviderPreferenceTypeV2.AVAILABILITY,
    )
    return availabilityMatchLevel?.match == ProviderPreferenceMatchV2.FULL
  })
}

/** Returns singular or plural form of availability copy text */
export const getUserAvailabilityPreferenceString = ({
  formatMessage,
  availabilities,
}: {
  formatMessage: IntlShape['formatMessage']
  availabilities?: string[] | null
}): string | undefined => {
  if (isEmptyAvailabilityPreferenceOption({ availabilities })) return undefined

  return availabilities && availabilities.length > 1
    ? formatMessage(MEMBER_PREFERENCE_LABELS.DaysAndTimes)
    : formatMessage(MEMBER_PREFERENCE_LABELS.DayAndTime)
}

/** Returns list of preferences the user has selected as an array of strings from the preference selection page */
export const getCurrentMemberPreferenceSelectionsToArray = ({
  intl,
  currentProviderPreferenceValues,
  shouldShowAvailabilityPreference,
  searchForCareProgramOptionFlow,
  areExpandedGlobalPreferencesEnabled,
  userCountryIsoCode,
}: {
  intl: IntlShape
  currentProviderPreferenceValues: ProviderPreferenceValues
  shouldShowAvailabilityPreference: boolean | undefined
  searchForCareProgramOptionFlow: S4C_PROGRAM_OPTION_FLOW
  areExpandedGlobalPreferencesEnabled?: boolean
  userCountryIsoCode?: string
}): string[] => {
  const arr: string[] = []
  const emptyPreferenceState = emptyProviderPreferenceStateWithAvailability
  // If user has not selected any preferences or if the FF is turned off
  if (!shouldShowAvailabilityPreference || isEqual(emptyPreferenceState, currentProviderPreferenceValues)) return arr

  // Order & filter preferences, we want to exclude the care language option since we are only checking
  // across preferences in the provider preference selection
  const preferencesList = getVisiblePreferenceCategories({
    searchForCareProgramOptionFlow,
    shouldShowAvailabilityPreference,
    shouldExcludeCareLanguageOption: true,
    areExpandedGlobalPreferencesEnabled,
    userCountryIsoCode,
  }).split(',')

  // Iterate through list of visible preferences
  preferencesList.forEach((preference) => {
    const value = (currentProviderPreferenceValues as Record<string, any>)[preference]
    const preferenceValue = Array.isArray(value) ? value : [value]
    if (preferenceValue.length > 0) {
      if (preference === 'isLgbtqia') {
        arr.push(intl.formatMessage(MEMBER_PREFERENCE_LABELS['LGBTQIA+']))
      } else if (preference === 'isExperiencedWithVeterans') {
        arr.push(intl.formatMessage(MEMBER_PREFERENCE_LABELS.isExperiencedWithVeterans))
      } else if (preference === 'availabilities') {
        if (!isEqual([MEMBER_PREFERENCE_IDS.FLEXIBLE], preferenceValue) && !isEqual([null], preferenceValue)) {
          arr.push(
            preferenceValue.length > 1
              ? intl.formatMessage(MEMBER_PREFERENCE_LABELS.DaysAndTimes)
              : intl.formatMessage(MEMBER_PREFERENCE_LABELS.DayAndTime),
          )
        }
      } else {
        preferenceValue.forEach((val: any) => {
          // In person text differs from preference label
          if (val === MEMBER_PREFERENCE_IDS.HIGHLIGHT_PROVIDERS_WHO_OFFER_IN_PERSON_SESSIONS) {
            arr.push(intl.formatMessage(MEMBER_PREFERENCE_LABELS.prefersInPerson))
          } else if (val) {
            arr.push(intl.formatMessage((MEMBER_PREFERENCE_LABELS as Record<string, MessageDescriptor>)[val]))
          }
        })
      }
    }
  })
  return arr
}

const isSearchingForXFlow = ({
  searchForCareProgramOptionFlow,
  flowToMatch,
}: {
  searchForCareProgramOptionFlow: S4C_PROGRAM_OPTION_FLOW
  flowToMatch: S4C_PROGRAM_OPTION_FLOW
}) => searchForCareProgramOptionFlow === flowToMatch

export const getIsSearchingForChild = ({
  searchForCareProgramOptionFlow,
}: {
  searchForCareProgramOptionFlow: S4C_PROGRAM_OPTION_FLOW
}) =>
  isSearchingForXFlow({ searchForCareProgramOptionFlow, flowToMatch: S4C_PROGRAM_OPTION_FLOW.PARENT_INITIATED_CHILD })

export const getIsSearchingForTeenAsParent = ({
  searchForCareProgramOptionFlow,
}: {
  searchForCareProgramOptionFlow: S4C_PROGRAM_OPTION_FLOW
}) =>
  isSearchingForXFlow({ searchForCareProgramOptionFlow, flowToMatch: S4C_PROGRAM_OPTION_FLOW.PARENT_INITIATED_TEEN })

export const getIsSearchingForTeenAsSelf = ({
  searchForCareProgramOptionFlow,
}: {
  searchForCareProgramOptionFlow: S4C_PROGRAM_OPTION_FLOW
}) => isSearchingForXFlow({ searchForCareProgramOptionFlow, flowToMatch: S4C_PROGRAM_OPTION_FLOW.SELF_INITIATED_TEENS })

export const getIsSearchingForChildOrSelfInitiatedTeenSearch = ({
  searchForCareProgramOptionFlow,
}: {
  searchForCareProgramOptionFlow: S4C_PROGRAM_OPTION_FLOW
}) =>
  getIsSearchingForChild({ searchForCareProgramOptionFlow }) ||
  getIsSearchingForTeenAsParent({ searchForCareProgramOptionFlow }) ||
  getIsSearchingForTeenAsSelf({ searchForCareProgramOptionFlow })

export const getAreMemberPreferencesApplicableForSearchForCareFlow = ({
  searchForCareProgramOptionFlow,
  shouldShowAvailabilityPreference,
  userCountryIsoCode,
  areExpandedGlobalPreferencesEnabled,
}: {
  searchForCareProgramOptionFlow: S4C_PROGRAM_OPTION_FLOW
  shouldShowAvailabilityPreference: boolean
  userCountryIsoCode?: string
  areExpandedGlobalPreferencesEnabled?: boolean
}) => {
  const visiblePreferences = getVisiblePreferenceCategories({
    searchForCareProgramOptionFlow,
    shouldShowAvailabilityPreference,
    shouldExcludeCareLanguageOption: true,
    areExpandedGlobalPreferencesEnabled,
    userCountryIsoCode,
  })

  return visiblePreferences.length > 0
}

export const getShouldShowMemberPreferencesEntryPoint = ({
  customerProductType,
  customerName,
  isUserInternational,
  isDirectPath,
  selectedTreatmentOption,
}: {
  customerProductType: PRODUCT_TYPE
  customerName: string
  isUserInternational: boolean
  isDirectPath: boolean
  selectedTreatmentOption: TREATMENT_OPTIONS_TYPE | null
}) => {
  // Ensure member preferences entry point is only shown for users that are eligible for the feature
  const shouldShowMemberPreferencesEntryPointSection = getShouldShowMemberPreferencesEntryPointSection({
    customerName,
    isUserInternational,
    isDirectPath,
    selectedTreatmentOption,
  })
  return shouldShowMemberPreferencesEntryPointSection && customerProductType !== PRODUCT_TYPE.SELECT
}

export const getIsGenderPreferenceConservative = ({ userCountryIsoCode }: { userCountryIsoCode: string }) => {
  const options = genders.countryConfig[userCountryIsoCode]?.options
  if (!options) return false

  const containsNonBinaryOption = options?.some((option) => option.id === MEMBER_PREFERENCE_IDS.NON_BINARY_PERSON)
  return !containsNonBinaryOption
}

export const getIsLGBTQIAPreferenceConservative = ({ userCountryIsoCode }: { userCountryIsoCode: string }) => {
  const options = lgbtqia.countryConfig[userCountryIsoCode]?.options

  if (!options) return false

  const containsConservativeLGBTQIAOption = options?.some(
    (option) => option.text === MEMBER_PREFERENCE_LABELS.experienceSupportingTheLGBTQIACommunity,
  )
  return containsConservativeLGBTQIAOption
}

export const getPreferenceCategoryForMixpanel = ({
  userCountryIsoCode,
  preferenceName,
}: {
  userCountryIsoCode: string
  preferenceName: string
}) => {
  let preferenceCategory =
    MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING_FOR_MIXPANEL[
      preferenceName as keyof typeof MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING_FOR_MIXPANEL
    ]
  if (preferenceName === MEMBER_PREFERENCE_NAMES.GENDER) {
    const isGenderPreferenceConservative = getIsGenderPreferenceConservative({ userCountryIsoCode })
    if (isGenderPreferenceConservative) {
      preferenceCategory = MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING_FOR_MIXPANEL.genderConservative
    }
  }
  if (preferenceName === MEMBER_PREFERENCE_NAMES.IS_LGBTQIA) {
    const isLGBTQIAPreferenceConservative = getIsLGBTQIAPreferenceConservative({ userCountryIsoCode })
    if (isLGBTQIAPreferenceConservative) {
      preferenceCategory = MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING_FOR_MIXPANEL.isLgbtqiaConservative
    }
  }

  return preferenceCategory
}
